import React, { useState, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight, Check, Loader2, BookOpen, Gamepad2, Brain, ShieldCheck, AlertCircle,
} from 'lucide-react';
import { supabase, isSupabaseConfigured, WAITLIST_TABLE } from '../lib/supabase';

/* ---------------------------------------------------------------- design tokens */
const GOLD = '#F5A623';
const GOLD_HOVER = '#E8940C';
const BG = '#0A0A0B';
const TEXT = '#F2F1ED';
const serif = { fontFamily: "'Calistoga', Georgia, serif" };
const mono = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };

/* Logo mark (transparent black-hole "O"). Height-driven, natural aspect.
   Falls back to a gold "O" if the image is missing. */
const Logo = ({ height = 30 }) => {
  const [ok, setOk] = useState(true);
  if (ok) {
    return (
      <img
        src="/optimus-mark.png" alt="Optimus" onError={() => setOk(false)}
        style={{ height, width: 'auto', display: 'block' }}
      />
    );
  }
  return <span className="font-black" style={{ ...serif, color: GOLD, fontSize: height, lineHeight: 1 }}>O</span>;
};

/* ---------------------------------------------------------------- content */
const PILLARS = [
  {
    n: '01', icon: BookOpen, title: 'Learn',
    body: 'Five-minute lessons on when, how and what to invest in. Quizzes, XP and streaks that actually stick.',
  },
  {
    n: '02', icon: Gamepad2, title: 'Practise',
    body: 'Trade real Indian market history in an arena where mistakes are free. Seasons, curated scenarios and blind rounds.',
  },
  {
    n: '03', icon: Brain, title: 'Get coached',
    body: 'Opti scores the reasoning behind every trade across six dimensions: thesis, risk, sizing, bias. Never the outcome.',
  },
];

const PROOF = [
  { stat: '13.2 cr', label: 'NSE investors, with 59% of new sign-ups under 30' },
  { stat: '1 in 3', label: 'investors have even moderate market knowledge' },
  { stat: '93%', label: 'of F&O traders lost money over FY22 to FY24' },
];

/* ---------------------------------------------------------------- validation */
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const digitsOf = (v) => v.replace(/\D/g, '');

/* ================================================================ Waitlist form */
const WaitlistForm = () => {
  const [form, setForm] = useState({ fullName: '', email: '', mobile: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [serverError, setServerError] = useState('');
  const [preview, setPreview] = useState(false);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((x) => ({ ...x, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.fullName.trim().length < 2) e.fullName = 'Please enter your name.';
    if (!isEmail(form.email.trim())) e.email = 'Enter a valid email address.';
    const d = digitsOf(form.mobile);
    const ten = d.startsWith('91') && d.length === 12 ? d.slice(2) : d;
    if (ten.length !== 10) e.mobile = 'Enter a 10-digit mobile number.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (status === 'loading') return;
    if (!validate()) return;

    setStatus('loading');
    setServerError('');
    const payload = {
      full_name: form.fullName.trim(),
      email: form.email.trim().toLowerCase(),
      mobile: '+91' + digitsOf(form.mobile).replace(/^91/, '').slice(-10),
      source: 'landing',
    };

    try {
      if (isSupabaseConfigured) {
        const { error } = await supabase.from(WAITLIST_TABLE).insert(payload);
        if (error) {
          if (error.code === '23505') { setStatus('success'); return; } // already on list
          throw error;
        }
        setStatus('success');
      } else {
        // Preview mode: Supabase not wired up yet. Store locally so the flow is
        // demonstrable, and tell the user plainly it isn't on the server.
        const key = 'optimus_waitlist_preview';
        const prev = JSON.parse(localStorage.getItem(key) || '[]');
        localStorage.setItem(key, JSON.stringify([...prev, { ...payload, at: Date.now() }]));
        await new Promise((r) => setTimeout(r, 650));
        setPreview(true);
        setStatus('success');
      }
    } catch (err) {
      setServerError(err?.message || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div
        className="rounded-2xl border p-8 sm:p-10 text-center"
        style={{ borderColor: 'rgba(245,166,35,0.28)', background: 'rgba(245,166,35,0.06)' }}
      >
        <div
          className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full"
          style={{ background: 'rgba(245,166,35,0.14)', border: `1px solid rgba(245,166,35,0.4)` }}
        >
          <Check size={26} style={{ color: GOLD }} />
        </div>
        <h3 className="text-2xl mb-2" style={{ ...serif, color: TEXT }}>You're on the list.</h3>
        <p className="text-[15px] leading-relaxed" style={{ color: 'rgba(242,241,237,0.6)' }}>
          We'll email <span style={{ color: TEXT }}>{form.email.trim().toLowerCase()}</span> the moment
          the Arena opens for beta.
        </p>
        {preview && (
          <p className="mt-4 text-xs" style={{ ...mono, color: 'rgba(242,241,237,0.4)' }}>
            preview mode: connect Supabase to store entries on the server
          </p>
        )}
      </div>
    );
  }

  const inputBase = 'w-full rounded-xl px-4 py-3.5 text-[15px] outline-none transition-colors';
  const inputStyle = (bad) => ({
    background: 'rgba(255,255,255,0.03)',
    border: `1px solid ${bad ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.12)'}`,
    color: TEXT,
  });

  return (
    <form onSubmit={onSubmit} noValidate className="rounded-2xl border p-6 sm:p-8"
      style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)' }}>
      {/* Name */}
      <div className="mb-4">
        <label htmlFor="wl-name" className="mb-1.5 block text-xs" style={{ ...mono, color: 'rgba(242,241,237,0.55)', letterSpacing: '0.06em' }}>
          FULL NAME
        </label>
        <input id="wl-name" type="text" autoComplete="name" value={form.fullName} onChange={set('fullName')}
          aria-invalid={!!errors.fullName} aria-describedby={errors.fullName ? 'err-name' : undefined}
          placeholder="Ananya Sharma" className={inputBase} style={inputStyle(errors.fullName)}
          onFocus={(e) => (e.target.style.borderColor = GOLD)}
          onBlur={(e) => (e.target.style.borderColor = errors.fullName ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.12)')} />
        {errors.fullName && <p id="err-name" className="mt-1.5 text-xs" style={{ color: '#F87171' }}>{errors.fullName}</p>}
      </div>

      {/* Email */}
      <div className="mb-4">
        <label htmlFor="wl-email" className="mb-1.5 block text-xs" style={{ ...mono, color: 'rgba(242,241,237,0.55)', letterSpacing: '0.06em' }}>
          EMAIL
        </label>
        <input id="wl-email" type="email" autoComplete="email" value={form.email} onChange={set('email')}
          aria-invalid={!!errors.email} aria-describedby={errors.email ? 'err-email' : undefined}
          placeholder="you@college.edu" className={inputBase} style={inputStyle(errors.email)}
          onFocus={(e) => (e.target.style.borderColor = GOLD)}
          onBlur={(e) => (e.target.style.borderColor = errors.email ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.12)')} />
        {errors.email && <p id="err-email" className="mt-1.5 text-xs" style={{ color: '#F87171' }}>{errors.email}</p>}
      </div>

      {/* Mobile */}
      <div className="mb-5">
        <label htmlFor="wl-mobile" className="mb-1.5 block text-xs" style={{ ...mono, color: 'rgba(242,241,237,0.55)', letterSpacing: '0.06em' }}>
          MOBILE
        </label>
        <div className="flex items-stretch rounded-xl" style={inputStyle(errors.mobile)}>
          <span className="flex items-center pl-4 pr-3 text-[15px] select-none" style={{ color: 'rgba(242,241,237,0.5)', borderRight: '1px solid rgba(255,255,255,0.1)' }}>+91</span>
          <input id="wl-mobile" type="tel" inputMode="numeric" autoComplete="tel" value={form.mobile} onChange={set('mobile')}
            aria-invalid={!!errors.mobile} aria-describedby={errors.mobile ? 'err-mobile' : undefined}
            placeholder="98765 43210" className="w-full bg-transparent px-4 py-3.5 text-[15px] outline-none" style={{ color: TEXT }} />
        </div>
        {errors.mobile && <p id="err-mobile" className="mt-1.5 text-xs" style={{ color: '#F87171' }}>{errors.mobile}</p>}
      </div>

      {status === 'error' && (
        <div className="mb-4 flex items-start gap-2 rounded-lg px-3.5 py-3 text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#FCA5A5' }}>
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{serverError}</span>
        </div>
      )}

      <button type="submit" disabled={status === 'loading'}
        className="group flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-[15px] font-semibold transition-all disabled:opacity-70"
        style={{ background: GOLD, color: '#1A1206', cursor: status === 'loading' ? 'wait' : 'pointer' }}
        onMouseEnter={(e) => { if (status !== 'loading') e.currentTarget.style.background = GOLD_HOVER; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = GOLD; }}>
        {status === 'loading' ? (<><Loader2 size={18} className="animate-spin" /> Joining…</>)
          : (<>Request early access <ArrowRight size={17} className="transition-transform group-hover:translate-x-0.5" /></>)}
      </button>

      <p className="mt-4 text-center text-xs leading-relaxed" style={{ color: 'rgba(242,241,237,0.4)' }}>
        Education product. Virtual money, historical data, for learning only. Not investment advice.
      </p>
    </form>
  );
};

/* ================================================================ Page */
const Landing = () => {
  const reduce = useReducedMotion();
  const waitlistRef = useRef(null);
  const howRef = useRef(null);

  const scrollTo = (ref) => () => ref.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });

  const fade = (delay = 0) =>
    reduce
      ? { initial: { opacity: 1 }, animate: { opacity: 1 } }
      : { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-80px' }, transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] } };

  return (
    <div style={{ background: BG, color: TEXT, minHeight: '100vh', overflowX: 'hidden' }}>
      {/* scoped styles */}
      <style>{`
        ::selection { background: ${GOLD}; color: #1A1206; }
        .arena-input::placeholder { color: rgba(242,241,237,0.32); }
        input::placeholder { color: rgba(242,241,237,0.32); }
        a, button { -webkit-tap-highlight-color: transparent; }
        :focus-visible { outline: 2px solid ${GOLD}; outline-offset: 2px; border-radius: 6px; }
      `}</style>

      {/* ---------------------------------------------------------- Nav */}
      <nav className="fixed inset-x-0 top-0 z-50" style={{ background: 'rgba(10,10,11,0.72)', backdropFilter: 'blur(14px)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <div className="flex items-center gap-2.5">
            <Logo height={34} />
            <span className="text-[13px] font-medium tracking-wide" style={{ ...mono, letterSpacing: '0.12em' }}>OPTIMUS ARENA</span>
          </div>
          <button onClick={scrollTo(waitlistRef)}
            className="rounded-full px-4 py-2 text-[13px] font-semibold transition-colors"
            style={{ background: 'rgba(245,166,35,0.12)', color: GOLD, border: `1px solid rgba(245,166,35,0.3)`, cursor: 'pointer' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(245,166,35,0.2)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(245,166,35,0.12)')}>
            Join the waitlist
          </button>
        </div>
      </nav>

      {/* ---------------------------------------------------------- Hero */}
      <header className="relative flex min-h-[100svh] items-center justify-center px-5 pt-24 pb-16 sm:px-8">
        {/* faint logo watermark + gold glow */}
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <img
            src="/optimus-mark.png" alt="" aria-hidden
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none"
            style={{
              width: 'min(2200px, 200vw)', opacity: 0.3,
              WebkitMaskImage: 'radial-gradient(ellipse 58% 62% at 50% 50%, #000 55%, transparent 86%)',
              maskImage: 'radial-gradient(ellipse 58% 62% at 50% 50%, #000 55%, transparent 86%)',
            }}
          />
          <div className="absolute left-1/2 top-[38%] h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: 'radial-gradient(circle, rgba(245,166,35,0.08) 0%, rgba(245,166,35,0) 68%)' }} />
          <div className="absolute inset-x-0 top-0 h-40" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.5), transparent)' }} />
        </div>

        <div className="relative z-10 mx-auto max-w-3xl text-center">
          <motion.p {...fade(0)} className="mb-6 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px]"
            style={{ ...mono, letterSpacing: '0.14em', color: 'rgba(242,241,237,0.7)', border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.02)' }}>
            <span style={{ width: 6, height: 6, borderRadius: 99, background: GOLD, display: 'inline-block' }} />
            PRIVATE BETA · IIT MADRAS · 2027
          </motion.p>

          <motion.h1 {...fade(0.06)} className="mb-6 text-[2.6rem] leading-[1.04] sm:text-6xl lg:text-[4.6rem]" style={{ ...serif, letterSpacing: '-0.01em' }}>
            We grade your thinking,
            <br />
            not your <span style={{ color: GOLD }}>luck</span>.
          </motion.h1>

          <motion.p {...fade(0.12)} className="mx-auto mb-9 max-w-xl text-[17px] leading-relaxed sm:text-[19px]" style={{ color: 'rgba(242,241,237,0.62)' }}>
            Optimus Arena is where you learn to invest, like a flight simulator for your
            money. Short lessons, real Indian market history to practise on, and an AI
            coach that scores the reasoning behind every trade.
          </motion.p>

          <motion.div {...fade(0.18)} className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button onClick={scrollTo(waitlistRef)}
              className="group flex w-full items-center justify-center gap-2 rounded-full px-7 py-3.5 text-[15px] font-semibold transition-all sm:w-auto"
              style={{ background: GOLD, color: '#1A1206', cursor: 'pointer', boxShadow: '0 8px 30px rgba(245,166,35,0.22)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = GOLD_HOVER)}
              onMouseLeave={(e) => (e.currentTarget.style.background = GOLD)}>
              Join the waitlist <ArrowRight size={17} className="transition-transform group-hover:translate-x-0.5" />
            </button>
            <button onClick={scrollTo(howRef)}
              className="flex w-full items-center justify-center gap-2 rounded-full px-7 py-3.5 text-[15px] font-medium transition-colors sm:w-auto"
              style={{ color: 'rgba(242,241,237,0.8)', border: '1px solid rgba(255,255,255,0.14)', cursor: 'pointer' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
              See how it works
            </button>
          </motion.div>

          <motion.p {...fade(0.26)} className="mt-8 text-[12px]" style={{ ...mono, color: 'rgba(242,241,237,0.4)', letterSpacing: '0.05em' }}>
            Free · Virtual money · No real trading · 18+
          </motion.p>
        </div>
      </header>

      {/* ---------------------------------------------------------- Proof / the gap */}
      <section className="mx-auto max-w-5xl px-5 py-16 sm:px-8" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <motion.p {...fade(0)} className="mb-9 text-center text-[11px]" style={{ ...mono, letterSpacing: '0.16em', color: 'rgba(242,241,237,0.45)' }}>
          THE GAP WE'RE CLOSING
        </motion.p>
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-6">
          {PROOF.map((p, i) => (
            <motion.div key={i} {...fade(i * 0.08)} className="text-center sm:text-left">
              <div className="mb-2 text-4xl sm:text-[2.75rem]" style={{ ...serif, color: TEXT }}>{p.stat}</div>
              <p className="mx-auto max-w-[240px] text-sm leading-relaxed sm:mx-0" style={{ color: 'rgba(242,241,237,0.5)' }}>{p.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------------------------------------------------------- How it works */}
      <section ref={howRef} className="mx-auto max-w-5xl scroll-mt-24 px-5 py-20 sm:px-8">
        <motion.div {...fade(0)} className="mb-14 max-w-2xl">
          <p className="mb-4 text-[11px]" style={{ ...mono, letterSpacing: '0.16em', color: GOLD }}>HOW IT WORKS</p>
          <h2 className="text-3xl leading-tight sm:text-[2.75rem]" style={serif}>
            Learn, practise, and get your reasoning graded, all in one loop.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl sm:grid-cols-3" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.07)' }}>
          {PILLARS.map((p, i) => (
            <motion.div key={p.n} {...fade(i * 0.08)} className="p-7 sm:p-8" style={{ background: BG }}>
              <div className="mb-6 flex items-center justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.22)' }}>
                  <p.icon size={19} style={{ color: GOLD }} />
                </div>
                <span className="text-sm" style={{ ...mono, color: 'rgba(242,241,237,0.28)' }}>{p.n}</span>
              </div>
              <h3 className="mb-2.5 text-xl" style={{ ...serif, color: TEXT }}>{p.title}</h3>
              <p className="text-[14.5px] leading-relaxed" style={{ color: 'rgba(242,241,237,0.55)' }}>{p.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------------------------------------------------------- Thesis pull-quote */}
      <section className="px-5 py-16 sm:px-8">
        <motion.blockquote {...fade(0)} className="mx-auto max-w-3xl text-center">
          <p className="text-[1.7rem] leading-[1.3] sm:text-4xl sm:leading-[1.28]" style={serif}>
            Pilots don't learn on passengers. Investors shouldn't
            learn on their <span style={{ color: GOLD }}>savings</span>.
          </p>
          <footer className="mt-6 text-[12px]" style={{ ...mono, letterSpacing: '0.1em', color: 'rgba(242,241,237,0.45)' }}>
            THE OPTIMUS ARENA THESIS
          </footer>
        </motion.blockquote>
      </section>

      {/* ---------------------------------------------------------- Waitlist */}
      <section ref={waitlistRef} className="scroll-mt-20 px-5 py-20 sm:px-8" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', background: 'linear-gradient(180deg, rgba(245,166,35,0.03), transparent 40%)' }}>
        <div className="mx-auto grid max-w-5xl grid-cols-1 items-start gap-12 lg:grid-cols-2 lg:gap-16">
          <motion.div {...fade(0)}>
            <p className="mb-4 text-[11px]" style={{ ...mono, letterSpacing: '0.16em', color: GOLD }}>THE WAITLIST</p>
            <h2 className="mb-4 text-3xl leading-tight sm:text-[2.75rem]" style={serif}>Be first into the Arena.</h2>
            <p className="mb-7 text-[16px] leading-relaxed" style={{ color: 'rgba(242,241,237,0.6)' }}>
              We're opening a private beta to Indian students in 2027. Leave your details and we'll
              invite you in early and shape the product around how you actually learn.
            </p>
            <ul className="space-y-3">
              {['Early access before the public launch', 'A founding-user badge inside the app', 'A direct line to the founding team'].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[15px]" style={{ color: 'rgba(242,241,237,0.72)' }}>
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ background: 'rgba(245,166,35,0.14)' }}>
                    <Check size={12} style={{ color: GOLD }} />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div {...fade(0.1)}>
            <WaitlistForm />
          </motion.div>
        </div>
      </section>

      {/* ---------------------------------------------------------- Footer */}
      <footer className="px-5 py-10 sm:px-8" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-5 sm:flex-row">
          <div className="flex items-center gap-2.5">
            <Logo height={26} />
            <span className="text-[12px]" style={{ ...mono, letterSpacing: '0.1em', color: 'rgba(242,241,237,0.6)' }}>OPTIMUS ARENA</span>
          </div>
          <p className="flex items-center gap-2 text-center text-[12px]" style={{ color: 'rgba(242,241,237,0.4)' }}>
            <ShieldCheck size={13} style={{ color: 'rgba(242,241,237,0.4)' }} />
            Investor-education product · Virtual money, historical data · Not investment advice
          </p>
        </div>
        <p className="mx-auto mt-6 max-w-6xl text-center text-[11px] sm:text-left" style={{ ...mono, color: 'rgba(242,241,237,0.28)' }}>
          © 2026 Optimus Arena · Chennai, India
        </p>
      </footer>
    </div>
  );
};

export default Landing;
